import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../lib/api";
import { sanitize } from "../../lib/sanitize";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { RichTextEditor } from "../ui/RichTextEditor";
import { Gear, FloppyDisk, Plus, Trash, PencilSimple, Check, X, UploadSimple, UserCircle, Image, FilePdf, Eye } from "@phosphor-icons/react";
import { SortableList } from "./SortableList";

const mediaUrl = (path) => {
  if (!path) return "";
  const clean = path.replace(/^quotations\//, "");
  return `${API}/quotations/media/${clean}`;
};

export default function QuotationConfig({ token, user }) {
  const [config, setConfig] = useState({});
  const [saving, setSaving] = useState(false);
  const [newTax, setNewTax] = useState({ name: "", rate: "", is_default: false });
  const [showNewTax, setShowNewTax] = useState(false);
  const [editTaxIdx, setEditTaxIdx] = useState(-1);
  const [editTax, setEditTax] = useState({});
  const [newTemplate, setNewTemplate] = useState({ name: "", content: "", font_color: "#1E293B", font_family: "Helvetica", font_size: 9, position: "after_totals" });
  const [showNewTemplate, setShowNewTemplate] = useState(false);
  const [editTemplateIdx, setEditTemplateIdx] = useState(-1);
  const [editTemplate, setEditTemplate] = useState({});
  const [newManager, setNewManager] = useState({ name: "", email: "", phone: "", whatsapp: "", position: "", photo_path: "" });
  const [showNewManager, setShowNewManager] = useState(false);
  const [editMgrIdx, setEditMgrIdx] = useState(-1);
  const [editMgr, setEditMgr] = useState({});
  const [newUrgency, setNewUrgency] = useState("");
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [uploadingDefaultAtt, setUploadingDefaultAtt] = useState(false);
  const [editAttIdx, setEditAttIdx] = useState(-1);
  const [editAtt, setEditAtt] = useState({});

  const fetchConfig = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/quotations/config`, { headers: { Authorization: `Bearer ${token}` } });
      setConfig(data || {});
    } catch (e) { process.env.NODE_ENV === "development" && console.error("Error fetching config:", e); }
  }, [token]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleSave = async (updatedConfig) => {
    setSaving(true);
    const toSave = updatedConfig || config;
    try {
      const { data } = await axios.put(`${API}/quotations/config`, toSave, { headers: { Authorization: `Bearer ${token}` } });
      setConfig(data || {});
      toast.success("Configuracion guardada");
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const update = (field, val) => setConfig(prev => ({ ...prev, [field]: val }));
  const isAdmin = user?.role === "admin";

  const taxConfigs = config.tax_configs || [];
  const termsTemplates = config.terms_templates || [];
  const managers = config.managers || [];
  const urgencyMessages = config.urgency_messages || [];
  const bannerImages = config.banner_images || [];

  const uploadPhoto = async (file, target) => {
    setUploadingPhoto(target);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await axios.post(`${API}/quotations/attachments/upload?category=photos`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      return data.path;
    } catch { toast.error("Error al subir foto"); return null; }
    finally { setUploadingPhoto(null); }
  };

  const uploadBannerImage = async (file) => {
    setUploadingBanner(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await axios.post(`${API}/quotations/attachments/upload?category=banners`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      const updated = { ...config, banner_images: [...bannerImages, { path: data.path, filename: data.filename, position: "end" }] };
      setConfig(updated);
      handleSave(updated);
    } catch { toast.error("Error al subir banner"); }
    finally { setUploadingBanner(false); }
  };

  // Tax CRUD
  const addTax = () => {
    if (!newTax.name.trim() || !newTax.rate) { toast.error("Nombre y porcentaje requeridos"); return; }
    const updated = { ...config, tax_configs: [...taxConfigs, { name: newTax.name, rate: parseFloat(newTax.rate), is_default: newTax.is_default }] };
    setConfig(updated); handleSave(updated);
    setNewTax({ name: "", rate: "", is_default: false }); setShowNewTax(false);
  };
  const removeTax = (idx) => { const updated = { ...config, tax_configs: taxConfigs.filter((_, i) => i !== idx) }; setConfig(updated); handleSave(updated); };
  const startEditTax = (idx) => { setEditTaxIdx(idx); setEditTax({ ...taxConfigs[idx] }); };
  const saveEditTax = () => {
    if (!editTax.name.trim() || !editTax.rate) { toast.error("Nombre y porcentaje requeridos"); return; }
    const nc = [...taxConfigs]; nc[editTaxIdx] = { ...nc[editTaxIdx], name: editTax.name, rate: parseFloat(editTax.rate), is_default: editTax.is_default };
    const updated = { ...config, tax_configs: nc }; setConfig(updated); handleSave(updated); setEditTaxIdx(-1);
  };

  // Template CRUD
  const FONT_OPTIONS = [
    { value: "Helvetica", label: "Helvetica" },
    { value: "Montserrat", label: "Montserrat" },
    { value: "Inter", label: "Inter" },
    { value: "Poppins", label: "Poppins" },
    { value: "Courier", label: "Courier" },
  ];
  const POSITION_OPTIONS = [
    { value: "after_totals", label: "Despues de totales (por defecto)" },
    { value: "before_items", label: "Antes de los productos" },
    { value: "before_managers", label: "Antes de los gerentes" },
    { value: "end", label: "Al final del documento" },
  ];

  const addTemplate = () => {
    if (!newTemplate.name.trim() || !newTemplate.content.trim()) { toast.error("Nombre y contenido requeridos"); return; }
    const tmpl = {
      name: newTemplate.name, content: newTemplate.content,
      font_color: newTemplate.font_color || "#1E293B",
      font_family: newTemplate.font_family || "Helvetica",
      font_size: parseInt(newTemplate.font_size) || 9,
      position: newTemplate.position || "after_totals",
    };
    const updated = { ...config, terms_templates: [...termsTemplates, tmpl] };
    setConfig(updated); handleSave(updated);
    setNewTemplate({ name: "", content: "", font_color: "#1E293B", font_family: "Helvetica", font_size: 9, position: "after_totals" });
    setShowNewTemplate(false);
  };
  const removeTemplate = (idx) => { const updated = { ...config, terms_templates: termsTemplates.filter((_, i) => i !== idx) }; setConfig(updated); handleSave(updated); };
  const startEditTemplate = (idx) => { setEditTemplateIdx(idx); setEditTemplate({ ...termsTemplates[idx] }); };
  const saveEditTemplate = () => {
    if (!editTemplate.name.trim() || !editTemplate.content.trim()) { toast.error("Nombre y contenido requeridos"); return; }
    const nt = [...termsTemplates]; nt[editTemplateIdx] = {
      ...nt[editTemplateIdx], name: editTemplate.name, content: editTemplate.content,
      font_color: editTemplate.font_color || "#1E293B",
      font_family: editTemplate.font_family || "Helvetica",
      font_size: parseInt(editTemplate.font_size) || 9,
      position: editTemplate.position || "after_totals",
    };
    const updated = { ...config, terms_templates: nt }; setConfig(updated); handleSave(updated); setEditTemplateIdx(-1);
  };

  // Manager CRUD
  const addManager = () => {
    if (!newManager.name.trim()) { toast.error("Nombre requerido"); return; }
    const updated = { ...config, managers: [...managers, { ...newManager }] };
    setConfig(updated); handleSave(updated);
    setNewManager({ name: "", email: "", phone: "", whatsapp: "", position: "", photo_path: "" }); setShowNewManager(false);
  };
  const removeManager = (idx) => { const updated = { ...config, managers: managers.filter((_, i) => i !== idx) }; setConfig(updated); handleSave(updated); };
  const startEditMgr = (idx) => { setEditMgrIdx(idx); setEditMgr({ ...managers[idx] }); };
  const saveEditMgr = () => {
    if (!editMgr.name.trim()) { toast.error("Nombre requerido"); return; }
    const nm = [...managers]; nm[editMgrIdx] = { ...nm[editMgrIdx], ...editMgr };
    const updated = { ...config, managers: nm }; setConfig(updated); handleSave(updated); setEditMgrIdx(-1);
  };

  const handleMgrPhoto = async (file, isNew, idx) => {
    const path = await uploadPhoto(file, isNew ? "new-mgr" : `mgr-${idx}`);
    if (!path) return;
    if (isNew) { setNewManager(p => ({ ...p, photo_path: path })); }
    else { setEditMgr(p => ({ ...p, photo_path: path })); }
  };

  // Urgency messages
  const addUrgency = () => {
    if (!newUrgency.trim()) return;
    const updated = { ...config, urgency_messages: [...urgencyMessages, newUrgency.trim()] };
    setConfig(updated); handleSave(updated); setNewUrgency("");
  };
  const removeUrgency = (idx) => { const updated = { ...config, urgency_messages: urgencyMessages.filter((_, i) => i !== idx) }; setConfig(updated); handleSave(updated); };

  const removeBanner = (idx) => { const updated = { ...config, banner_images: bannerImages.filter((_, i) => i !== idx) }; setConfig(updated); handleSave(updated); };
  const updateBannerPosition = (idx, pos) => {
    const nb = [...bannerImages]; nb[idx] = { ...nb[idx], position: pos };
    const updated = { ...config, banner_images: nb }; setConfig(updated); handleSave(updated);
  };

  // Generic reorder handler
  const reorderList = (field, newList) => {
    const updated = { ...config, [field]: newList };
    setConfig(updated); handleSave(updated);
  };

  // Default Attachments CRUD
  const defaultAttachments = config.default_attachments || [];
  const isPdfAtt = (att) => (att?.content_type === "application/pdf" || (att?.filename || att?.path || "").toLowerCase().endsWith(".pdf"));

  const uploadDefaultAttachment = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const allowed = ["image/", "application/pdf"];
    const maxSize = 10 * 1024 * 1024;
    const valid = files.filter(f => {
      if (!allowed.some(t => f.type.startsWith(t))) { toast.error(`${f.name}: solo imagenes y PDFs`); return false; }
      if (f.size > maxSize) { toast.error(`${f.name}: max 10MB`); return false; }
      return true;
    });
    if (!valid.length) return;
    setUploadingDefaultAtt(true);
    try {
      const newAtts = [...defaultAttachments];
      for (const file of valid) {
        const formData = new FormData();
        formData.append("file", file);
        const { data } = await axios.post(`${API}/quotations/attachments/upload`, formData, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        });
        const isPdf = file.type === "application/pdf";
        newAtts.push({
          path: data.path,
          filename: data.filename,
          content_type: data.content_type,
          label: isPdf ? "CATALOGO" : "",
          link_text: isPdf ? "Dale click para ver el CATALOGO" : "",
          is_default: true,
        });
      }
      const updated = { ...config, default_attachments: newAtts };
      setConfig(updated); handleSave(updated);
      toast.success(valid.length > 1 ? `${valid.length} archivos agregados` : "Archivo agregado");
    } catch { toast.error("Error al subir archivo"); }
    finally { setUploadingDefaultAtt(false); e.target.value = ""; }
  };

  const removeDefaultAtt = (idx) => {
    const updated = { ...config, default_attachments: defaultAttachments.filter((_, i) => i !== idx) };
    setConfig(updated); handleSave(updated);
  };

  const startEditAtt = (idx) => { setEditAttIdx(idx); setEditAtt({ ...defaultAttachments[idx] }); };
  const saveEditAtt = () => {
    const na = [...defaultAttachments];
    na[editAttIdx] = { ...na[editAttIdx], label: editAtt.label, link_text: editAtt.link_text };
    const updated = { ...config, default_attachments: na };
    setConfig(updated); handleSave(updated); setEditAttIdx(-1);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="quotation-config">
      <div className="flex items-center gap-2">
        <Gear size={22} className="text-blue-600" />
        <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: "Montserrat, sans-serif" }}>Configuracion de Cotizaciones</h2>
      </div>

      {/* Company Info */}
      <div className="bg-white rounded-xl border p-6 space-y-5">
        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider" style={{ fontFamily: "Montserrat, sans-serif" }}>Datos Comerciales</h3>

        {/* Logo Upload */}
        {isAdmin && (
          <div className="flex items-center gap-4 pb-4 border-b">
            <div className="relative">
              {config.logo_path ? (
                <img src={mediaUrl(config.logo_path)} alt="Logo" className="w-24 h-20 object-contain border rounded-lg bg-white p-1" />
              ) : (
                <div className="w-24 h-20 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center bg-slate-50">
                  <Image size={28} className="text-slate-300" />
                </div>
              )}
              <label className="absolute -bottom-2 -right-2 w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center cursor-pointer hover:bg-blue-700 shadow-md">
                <UploadSimple size={14} className="text-white" />
                <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const path = await uploadPhoto(file, "logo");
                  if (path) { const u = { ...config, logo_path: path }; setConfig(u); handleSave(u); }
                  e.target.value = "";
                }} data-testid="logo-upload" />
              </label>
            </div>
            <div>
              <p className="font-semibold text-sm text-slate-700">Logo de la Empresa</p>
              <p className="text-xs text-slate-400">Se muestra al inicio del PDF de cotizaciones</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            ["company_name", "Nombre de Empresa"],
            ["company_phone", "Telefono Fijo"],
            ["company_cellphone", "Celular"],
            ["company_email", "Email"],
          ].map(([k, l]) => (
            <div key={k}>
              <label className="text-sm font-medium text-slate-600">{l}</label>
              <input value={config[k] || ""} onChange={e => update(k, e.target.value)} disabled={!isAdmin}
                className="w-full px-3 py-2 border rounded-lg text-sm mt-1 disabled:bg-slate-50" data-testid={`config-${k}`} />
            </div>
          ))}
          <div className="sm:col-span-2">
            <label className="text-sm font-medium text-slate-600">Direccion de la Empresa</label>
            <input value={config.company_address || ""} onChange={e => update("company_address", e.target.value)} disabled={!isAdmin}
              className="w-full px-3 py-2 border rounded-lg text-sm mt-1 disabled:bg-slate-50" data-testid="config-company_address" />
          </div>
        </div>
      </div>

      {/* Fiscal */}
      <div className="bg-white rounded-xl border p-6 space-y-5">
        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider" style={{ fontFamily: "Montserrat, sans-serif" }}>Datos Fiscales</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[["fiscal_name", "Razon Social"], ["fiscal_rfc", "RFC"]].map(([k, l]) => (
            <div key={k}><label className="text-sm font-medium text-slate-600">{l}</label>
              <input value={config[k] || ""} onChange={e => update(k, e.target.value)} disabled={!isAdmin} className="w-full px-3 py-2 border rounded-lg text-sm mt-1 disabled:bg-slate-50" data-testid={`config-${k}`} /></div>
          ))}
          <div>
            <label className="text-sm font-medium text-slate-600">Regimen Fiscal</label>
            <input value={config.fiscal_regime || ""} onChange={e => update("fiscal_regime", e.target.value)} disabled={!isAdmin}
              placeholder="Ej: Regimen General de Ley" className="w-full px-3 py-2 border rounded-lg text-sm mt-1 disabled:bg-slate-50" data-testid="config-fiscal_regime" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-600">Direccion Fiscal</label>
            <input value={config.fiscal_address || ""} onChange={e => update("fiscal_address", e.target.value)} disabled={!isAdmin}
              className="w-full px-3 py-2 border rounded-lg text-sm mt-1 disabled:bg-slate-50" data-testid="config-fiscal_address" />
          </div>
        </div>
      </div>

      {/* Managers */}
      {isAdmin && (
        <div className="bg-white rounded-xl border p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider" style={{ fontFamily: "Montserrat, sans-serif" }}>Gerentes / Contactos de Venta</h3>
            <Button variant="outline" size="sm" onClick={() => setShowNewManager(!showNewManager)} data-testid="add-manager-btn"><Plus size={14} className="mr-1" /> Nuevo</Button>
          </div>
          <p className="text-xs text-slate-400">Se mostrarán al final de cada cotización con foto, datos de contacto y QR de WhatsApp.</p>

          {showNewManager && (
            <div className="bg-slate-50 p-4 rounded-lg space-y-3">
              <div className="flex items-center gap-4">
                <div className="relative shrink-0">
                  {newManager.photo_path ? (
                    <img src={mediaUrl(newManager.photo_path)} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-blue-300" />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-slate-200 flex items-center justify-center"><UserCircle size={32} className="text-slate-400" /></div>
                  )}
                  <label className="absolute -bottom-1 -right-1 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center cursor-pointer hover:bg-blue-700">
                    <UploadSimple size={12} className="text-white" />
                    <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && handleMgrPhoto(e.target.files[0], true)} />
                  </label>
                </div>
                <div className="flex-1 grid grid-cols-2 gap-2">
                  <input placeholder="Nombre *" value={newManager.name} onChange={e => setNewManager(p => ({ ...p, name: e.target.value }))} className="px-2 py-1.5 border rounded text-sm" data-testid="new-mgr-name" />
                  <input placeholder="Puesto" value={newManager.position} onChange={e => setNewManager(p => ({ ...p, position: e.target.value }))} className="px-2 py-1.5 border rounded text-sm" />
                  <input placeholder="Email" value={newManager.email} onChange={e => setNewManager(p => ({ ...p, email: e.target.value }))} className="px-2 py-1.5 border rounded text-sm" />
                  <input placeholder="Telefono" value={newManager.phone} onChange={e => setNewManager(p => ({ ...p, phone: e.target.value }))} className="px-2 py-1.5 border rounded text-sm" />
                  <input placeholder="WhatsApp (con lada, ej: 5215512345678)" value={newManager.whatsapp} onChange={e => setNewManager(p => ({ ...p, whatsapp: e.target.value }))} className="col-span-2 px-2 py-1.5 border rounded text-sm" />
                </div>
              </div>
              <Button size="sm" onClick={addManager} className="bg-blue-600" data-testid="save-new-manager">Agregar Gerente</Button>
            </div>
          )}

          <SortableList items={managers} onReorder={(newList) => reorderList("managers", newList)} getId={(m, i) => m.id || `mgr-${i}`}>
            {(mgr, idx) => (
              <div className="flex items-center gap-3 bg-slate-50 rounded-lg px-4 py-3">
                {editMgrIdx === idx ? (
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-3">
                      <div className="relative shrink-0">
                        {editMgr.photo_path ? <img src={mediaUrl(editMgr.photo_path)} alt="" className="w-12 h-12 rounded-full object-cover" /> : <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center"><UserCircle size={24} className="text-slate-400" /></div>}
                        <label className="absolute -bottom-1 -right-1 w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center cursor-pointer"><UploadSimple size={10} className="text-white" />
                          <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && handleMgrPhoto(e.target.files[0], false, idx)} /></label>
                      </div>
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <input value={editMgr.name} onChange={e => setEditMgr(p => ({ ...p, name: e.target.value }))} placeholder="Nombre" className="px-2 py-1 border rounded text-sm" />
                        <input value={editMgr.position || ""} onChange={e => setEditMgr(p => ({ ...p, position: e.target.value }))} placeholder="Puesto" className="px-2 py-1 border rounded text-sm" />
                        <input value={editMgr.email || ""} onChange={e => setEditMgr(p => ({ ...p, email: e.target.value }))} placeholder="Email" className="px-2 py-1 border rounded text-sm" />
                        <input value={editMgr.whatsapp || ""} onChange={e => setEditMgr(p => ({ ...p, whatsapp: e.target.value }))} placeholder="WhatsApp" className="px-2 py-1 border rounded text-sm" />
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={saveEditMgr} className="p-1 text-green-600 hover:bg-green-50 rounded"><Check size={16} /></button>
                      <button onClick={() => setEditMgrIdx(-1)} className="p-1 text-slate-400 hover:bg-slate-100 rounded"><X size={16} /></button>
                    </div>
                  </div>
                ) : (
                  <>
                    {mgr.photo_path ? <img src={mediaUrl(mgr.photo_path)} alt="" className="w-10 h-10 rounded-full object-cover" /> : <UserCircle size={40} className="text-slate-300" />}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-slate-700">{mgr.name} {mgr.position && <span className="text-slate-400 font-normal">- {mgr.position}</span>}</p>
                      <p className="text-xs text-slate-500 truncate">{[mgr.email, mgr.phone, mgr.whatsapp ? `WA: ${mgr.whatsapp}` : null].filter(Boolean).join(" | ")}</p>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => startEditMgr(idx)} className="p-1.5 rounded hover:bg-blue-50 text-blue-600"><PencilSimple size={14} /></button>
                      <button onClick={() => removeManager(idx)} className="p-1.5 rounded hover:bg-red-50 text-red-500"><Trash size={14} /></button>
                    </div>
                  </>
                )}
              </div>
            )}
          </SortableList>
        </div>
      )}

      {/* Urgency Messages */}
      {isAdmin && (
        <div className="bg-white rounded-xl border p-6 space-y-4">
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider" style={{ fontFamily: "Montserrat, sans-serif" }}>Mensajes de Urgencia / Promocion</h3>
          <p className="text-xs text-slate-400">Aparecerán de forma prominente en todas las cotizaciones junto a la vigencia.</p>
          <div className="flex gap-2">
            <input value={newUrgency} onChange={e => setNewUrgency(e.target.value)} placeholder="Ej: Promocion del mes - Precios sujetos a disponibilidad"
              className="flex-1 px-3 py-2 border rounded-lg text-sm" data-testid="new-urgency" onKeyDown={e => e.key === "Enter" && addUrgency()} />
            <Button size="sm" onClick={addUrgency} className="bg-blue-600">Agregar</Button>
          </div>
          <SortableList items={urgencyMessages} onReorder={(newList) => reorderList("urgency_messages", newList)} getId={(_, i) => `urg-${i}`}>
            {(msg, idx) => (
              <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <span className="text-sm text-amber-800 font-medium">{msg}</span>
                <button onClick={() => removeUrgency(idx)} className="p-1 rounded hover:bg-amber-100 text-amber-600"><Trash size={14} /></button>
              </div>
            )}
          </SortableList>
        </div>
      )}

      {/* Banner Images */}
      {isAdmin && (
        <div className="bg-white rounded-xl border p-6 space-y-4">
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider" style={{ fontFamily: "Montserrat, sans-serif" }}>Banners Promocionales</h3>
          <p className="text-xs text-slate-400">Configura la posicion de cada banner en el PDF. Arrastra para reordenar.</p>
          <label className={`flex items-center justify-center gap-2 px-4 py-6 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${uploadingBanner ? "bg-blue-50 border-blue-300" : "border-slate-300 hover:border-blue-400"}`}>
            <input type="file" accept="image/*" className="hidden" disabled={uploadingBanner} onChange={e => e.target.files?.[0] && uploadBannerImage(e.target.files[0])} data-testid="banner-upload" />
            <Image size={20} className="text-blue-500" />
            <span className="text-sm text-slate-600">{uploadingBanner ? "Subiendo..." : "Agregar banner"}</span>
          </label>
          {bannerImages.length > 0 && (
            <SortableList items={bannerImages} onReorder={(newList) => reorderList("banner_images", newList)} getId={(b, i) => b.id || `banner-${i}`}>
              {(b, idx) => (
                <div className="border rounded-lg overflow-hidden bg-slate-50">
                  <div className="relative group">
                    <img src={mediaUrl(b.path || b)} alt={b.filename || `Banner ${idx + 1}`} className="w-full h-24 object-cover" />
                    <button onClick={() => removeBanner(idx)} className="absolute top-2 right-2 p-1.5 bg-red-600 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity"><Trash size={14} /></button>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-2">
                    <span className="text-xs text-slate-500 shrink-0">Posicion:</span>
                    <select value={b.position || "end"} onChange={e => updateBannerPosition(idx, e.target.value)} className="flex-1 px-2 py-1 border rounded text-xs" data-testid={`banner-position-${idx}`}>
                      {POSITION_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </div>
                </div>
              )}
            </SortableList>
          )}
        </div>
      )}

      {/* Tax Configs */}
      {isAdmin && (
        <div className="bg-white rounded-xl border p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider" style={{ fontFamily: "Montserrat, sans-serif" }}>Impuestos Configurados</h3>
            <Button variant="outline" size="sm" onClick={() => setShowNewTax(!showNewTax)} data-testid="add-tax-btn"><Plus size={14} className="mr-1" /> Nuevo</Button>
          </div>
          {showNewTax && (
            <div className="flex flex-wrap gap-2 items-end bg-slate-50 p-3 rounded-lg">
              <div><label className="text-xs text-slate-500">Nombre</label><input value={newTax.name} onChange={e => setNewTax(p => ({ ...p, name: e.target.value }))} placeholder="IVA" className="w-32 px-2 py-1.5 border rounded text-sm mt-1" data-testid="new-tax-name" /></div>
              <div><label className="text-xs text-slate-500">%</label><input type="number" value={newTax.rate} onChange={e => setNewTax(p => ({ ...p, rate: e.target.value }))} placeholder="16" className="w-20 px-2 py-1.5 border rounded text-sm mt-1 text-center" data-testid="new-tax-rate" /></div>
              <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer"><input type="checkbox" checked={newTax.is_default} onChange={e => setNewTax(p => ({ ...p, is_default: e.target.checked }))} className="rounded" /> Defecto</label>
              <Button size="sm" onClick={addTax} className="bg-blue-600" data-testid="save-new-tax">Agregar</Button>
            </div>
          )}
          <div className="space-y-2">
            {taxConfigs.length === 0 && <p className="text-sm text-slate-400">No hay impuestos configurados.</p>}
            {taxConfigs.map((tc, idx) => (
              <div key={tc.id || idx} className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-2.5">
                {editTaxIdx === idx ? (
                  <div className="flex flex-wrap gap-2 items-center flex-1">
                    <input value={editTax.name} onChange={e => setEditTax(p => ({ ...p, name: e.target.value }))} className="w-32 px-2 py-1 border rounded text-sm" />
                    <input type="number" value={editTax.rate} onChange={e => setEditTax(p => ({ ...p, rate: e.target.value }))} className="w-20 px-2 py-1 border rounded text-sm text-center" />
                    <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={editTax.is_default} onChange={e => setEditTax(p => ({ ...p, is_default: e.target.checked }))} className="rounded" /> Defecto</label>
                    <button onClick={saveEditTax} className="p-1 text-green-600 hover:bg-green-50 rounded"><Check size={16} /></button>
                    <button onClick={() => setEditTaxIdx(-1)} className="p-1 text-slate-400 hover:bg-slate-100 rounded"><X size={16} /></button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-3"><span className="font-medium text-sm">{tc.name}</span><span className="text-sm text-blue-600 font-bold">{tc.rate}%</span>{tc.is_default && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Defecto</span>}</div>
                    <div className="flex gap-1"><button onClick={() => startEditTax(idx)} className="p-1.5 rounded hover:bg-blue-50 text-blue-600"><PencilSimple size={14} /></button><button onClick={() => removeTax(idx)} className="p-1.5 rounded hover:bg-red-50 text-red-500"><Trash size={14} /></button></div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Terms Templates */}
      {isAdmin && (
        <div className="bg-white rounded-xl border p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider" style={{ fontFamily: "Montserrat, sans-serif" }}>Plantillas de Terminos y Condiciones</h3>
            <Button variant="outline" size="sm" onClick={() => setShowNewTemplate(!showNewTemplate)} data-testid="add-template-btn"><Plus size={14} className="mr-1" /> Nueva</Button>
          </div>
          {showNewTemplate && (
            <div className="bg-slate-50 p-4 rounded-lg space-y-3 border border-slate-200">
              <input value={newTemplate.name} onChange={e => setNewTemplate(p => ({ ...p, name: e.target.value }))} placeholder="Nombre de la plantilla" className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="new-template-name" />
              <RichTextEditor value={newTemplate.content || ""} onChange={v => setNewTemplate(p => ({ ...p, content: v }))} placeholder="Contenido de los terminos..." testId="new-template-content" minHeight="100px" />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Color</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={newTemplate.font_color || "#1E293B"} onChange={e => setNewTemplate(p => ({ ...p, font_color: e.target.value }))} className="w-8 h-8 rounded border cursor-pointer" data-testid="new-template-color" />
                    <input type="text" value={newTemplate.font_color || "#1E293B"} onChange={e => setNewTemplate(p => ({ ...p, font_color: e.target.value }))} className="flex-1 px-2 py-1.5 border rounded text-xs font-mono" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Fuente</label>
                  <select value={newTemplate.font_family || "Helvetica"} onChange={e => setNewTemplate(p => ({ ...p, font_family: e.target.value }))} className="w-full px-2 py-1.5 border rounded-lg text-sm" data-testid="new-template-font">
                    {FONT_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Tamano</label>
                  <input type="number" min={6} max={18} value={newTemplate.font_size || 9} onChange={e => setNewTemplate(p => ({ ...p, font_size: e.target.value }))} className="w-full px-2 py-1.5 border rounded-lg text-sm" data-testid="new-template-size" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Posicion en PDF</label>
                  <select value={newTemplate.position || "after_totals"} onChange={e => setNewTemplate(p => ({ ...p, position: e.target.value }))} className="w-full px-2 py-1.5 border rounded-lg text-sm" data-testid="new-template-position">
                    {POSITION_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-4 pt-1">
                <span className="text-xs text-slate-500">Vista previa:</span>
                <div style={{ color: newTemplate.font_color || "#1E293B", fontFamily: newTemplate.font_family || "Helvetica", fontSize: `${newTemplate.font_size || 9}px` }} className="border-b border-dashed px-2 py-1 prose prose-sm max-w-none">
                  {newTemplate.content ? <span dangerouslySetInnerHTML={sanitize(newTemplate.content.substring(0, 200))} /> : <span className="text-slate-400">Texto de ejemplo...</span>}
                </div>
              </div>
              <Button size="sm" onClick={addTemplate} className="bg-blue-600" data-testid="save-new-template">Agregar Plantilla</Button>
            </div>
          )}
          <div>
            {termsTemplates.length === 0 && <p className="text-sm text-slate-400">No hay plantillas.</p>}
            <SortableList items={termsTemplates} onReorder={(newList) => reorderList("terms_templates", newList)} getId={(t, i) => t.id || `tmpl-${i}`}>
              {(tmpl, idx) => (
                <div className="bg-slate-50 rounded-lg px-4 py-3">
                  {editTemplateIdx === idx ? (
                    <div className="space-y-3">
                      <input value={editTemplate.name} onChange={e => setEditTemplate(p => ({ ...p, name: e.target.value }))} className="w-full px-3 py-1.5 border rounded text-sm" />
                      <RichTextEditor value={editTemplate.content || ""} onChange={v => setEditTemplate(p => ({ ...p, content: v }))} testId={`edit-template-content-${idx}`} minHeight="100px" />
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div>
                          <label className="text-xs font-medium text-slate-500 mb-1 block">Color</label>
                          <div className="flex items-center gap-2">
                            <input type="color" value={editTemplate.font_color || "#1E293B"} onChange={e => setEditTemplate(p => ({ ...p, font_color: e.target.value }))} className="w-8 h-8 rounded border cursor-pointer" data-testid={`edit-template-color-${idx}`} />
                            <input type="text" value={editTemplate.font_color || "#1E293B"} onChange={e => setEditTemplate(p => ({ ...p, font_color: e.target.value }))} className="flex-1 px-2 py-1.5 border rounded text-xs font-mono" />
                          </div>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-slate-500 mb-1 block">Fuente</label>
                          <select value={editTemplate.font_family || "Helvetica"} onChange={e => setEditTemplate(p => ({ ...p, font_family: e.target.value }))} className="w-full px-2 py-1.5 border rounded-lg text-sm" data-testid={`edit-template-font-${idx}`}>
                            {FONT_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-slate-500 mb-1 block">Tamano</label>
                          <input type="number" min={6} max={18} value={editTemplate.font_size || 9} onChange={e => setEditTemplate(p => ({ ...p, font_size: e.target.value }))} className="w-full px-2 py-1.5 border rounded-lg text-sm" data-testid={`edit-template-size-${idx}`} />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-slate-500 mb-1 block">Posicion en PDF</label>
                          <select value={editTemplate.position || "after_totals"} onChange={e => setEditTemplate(p => ({ ...p, position: e.target.value }))} className="w-full px-2 py-1.5 border rounded-lg text-sm" data-testid={`edit-template-position-${idx}`}>
                            {POSITION_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="flex gap-1"><button onClick={saveEditTemplate} className="p-1 text-green-600 hover:bg-green-50 rounded"><Check size={16} /></button><button onClick={() => setEditTemplateIdx(-1)} className="p-1 text-slate-400 hover:bg-slate-100 rounded"><X size={16} /></button></div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-sm text-slate-700">{tmpl.name}</p>
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">
                            {POSITION_OPTIONS.find(p => p.value === (tmpl.position || "after_totals"))?.label || tmpl.position}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1 line-clamp-2 prose prose-sm max-w-none" dangerouslySetInnerHTML={sanitize(tmpl.content || "")} />
                        <div className="flex items-center gap-3 mt-2">
                          <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                            <span className="w-3 h-3 rounded-full border" style={{ backgroundColor: tmpl.font_color || "#1E293B" }}></span>
                            {tmpl.font_family || "Helvetica"} · {tmpl.font_size || 9}px
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-1 shrink-0 ml-2"><button onClick={() => startEditTemplate(idx)} className="p-1.5 rounded hover:bg-blue-50 text-blue-600" data-testid={`edit-template-${idx}`}><PencilSimple size={14} /></button><button onClick={() => removeTemplate(idx)} className="p-1.5 rounded hover:bg-red-50 text-red-500" data-testid={`delete-template-${idx}`}><Trash size={14} /></button></div>
                    </div>
                  )}
                </div>
              )}
            </SortableList>
          </div>
        </div>
      )}

      {/* Default Attachments */}
      {isAdmin && (
        <div className="bg-white rounded-xl border p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider" style={{ fontFamily: "Montserrat, sans-serif" }}>Archivos Adjuntos Predeterminados</h3>
          </div>
          <p className="text-xs text-slate-400">Estos archivos se incluiran automaticamente en cada nueva cotizacion. Configure el titulo y texto del enlace para los PDFs.</p>

          <label className={`flex items-center justify-center gap-2 px-4 py-6 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${uploadingDefaultAtt ? "bg-blue-50 border-blue-300" : "border-slate-300 hover:border-blue-400 hover:bg-blue-50/30"}`}>
            <input type="file" accept="image/*,.pdf,application/pdf" multiple onChange={uploadDefaultAttachment} className="hidden" disabled={uploadingDefaultAtt} data-testid="default-att-upload" />
            {uploadingDefaultAtt ? (
              <span className="text-sm text-blue-600 font-medium">Subiendo...</span>
            ) : (
              <>
                <UploadSimple size={18} className="text-blue-500" />
                <span className="text-sm text-slate-600">Agregar imagenes o catalogos PDF (max 10MB)</span>
              </>
            )}
          </label>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {defaultAttachments.length === 0 && <p className="text-sm text-slate-400 col-span-full">No hay archivos predeterminados.</p>}
            {defaultAttachments.map((att, idx) => {
              const pdf = isPdfAtt(att);
              const attUrl = `${API}/quotations/attachments/${(att.path || "").replace("quotations/attachments/", "")}`;
              const isEditing = editAttIdx === idx;
              return (
                <div key={att.id || idx} className="relative group border rounded-lg overflow-hidden bg-white hover:shadow-md transition-shadow" data-testid={`default-att-${idx}`}>
                  {/* Thumbnail */}
                  {pdf ? (
                    <div className="w-full h-32 flex flex-col items-center justify-center bg-red-50 gap-1 cursor-pointer" onClick={() => !isEditing && window.open(attUrl, "_blank")}>
                      <FilePdf size={32} className="text-red-500" weight="duotone" />
                      {att.label && <span className="text-[10px] font-bold text-red-800 bg-red-100 px-1.5 py-0.5 rounded">{att.label}</span>}
                      {att.link_text && <span className="text-[9px] text-blue-600 px-2 text-center line-clamp-1">{att.link_text}</span>}
                    </div>
                  ) : (
                    <img src={attUrl} alt="" className="w-full h-32 object-cover cursor-pointer" onClick={() => window.open(attUrl, "_blank")} />
                  )}

                  {/* Hover overlay with actions */}
                  {!isEditing && (
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                      <button onClick={() => window.open(attUrl, "_blank")} className="p-2 bg-white/90 rounded-full text-blue-600 hover:bg-white shadow" title="Ver"><Eye size={16} /></button>
                      {pdf && <button onClick={() => startEditAtt(idx)} className="p-2 bg-white/90 rounded-full text-amber-600 hover:bg-white shadow" title="Editar titulo" data-testid={`edit-att-btn-${idx}`}><PencilSimple size={16} /></button>}
                      <button onClick={() => removeDefaultAtt(idx)} className="p-2 bg-red-600 rounded-full text-white hover:bg-red-700 shadow" title="Eliminar" data-testid={`delete-att-${idx}`}><Trash size={16} /></button>
                    </div>
                  )}

                  {/* Footer: filename */}
                  <p className="text-[10px] text-slate-500 px-2 py-1.5 truncate bg-white border-t">{att.filename || "Archivo"}</p>

                  {/* Edit overlay for PDFs */}
                  {isEditing && (
                    <div className="absolute inset-0 bg-white/95 z-10 flex flex-col p-3 gap-2 justify-center">
                      <div>
                        <label className="text-[10px] font-semibold text-slate-500">Titulo</label>
                        <input value={editAtt.label || ""} onChange={e => setEditAtt(p => ({ ...p, label: e.target.value }))}
                          placeholder="CATALOGO" className="w-full px-2 py-1 border rounded text-xs mt-0.5" autoFocus data-testid={`edit-att-label-${idx}`} />
                      </div>
                      <div>
                        <label className="text-[10px] font-semibold text-slate-500">Texto enlace</label>
                        <input value={editAtt.link_text || ""} onChange={e => setEditAtt(p => ({ ...p, link_text: e.target.value }))}
                          placeholder="Dale click para ver..." className="w-full px-2 py-1 border rounded text-xs mt-0.5" data-testid={`edit-att-linktext-${idx}`} />
                      </div>
                      <div className="flex gap-1 justify-center pt-1">
                        <button onClick={saveEditAtt} className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700" data-testid={`save-att-${idx}`}><Check size={12} className="inline mr-1" />OK</button>
                        <button onClick={() => setEditAttIdx(-1)} className="px-3 py-1 bg-slate-200 text-slate-600 text-xs rounded hover:bg-slate-300"><X size={12} className="inline mr-1" />Cancelar</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Defaults */}
      <div className="bg-white rounded-xl border p-6 space-y-5">
        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider" style={{ fontFamily: "Montserrat, sans-serif" }}>Valores Predeterminados</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className="text-sm font-medium text-slate-600">Validez por defecto (dias)</label>
            <input type="number" value={config.default_validity_days || ""} onChange={e => update("default_validity_days", parseInt(e.target.value) || null)} disabled={!isAdmin} className="w-full px-3 py-2 border rounded-lg text-sm mt-1 disabled:bg-slate-50" /></div>
        </div>
        <div><label className="text-sm font-medium text-slate-600 mb-1 block">Terminos de pago por defecto</label>
          <RichTextEditor value={config.default_payment_terms || ""} onChange={v => update("default_payment_terms", v)} disabled={!isAdmin} placeholder="Ej: 50% al inicio, 50% contra entrega" compact testId="config-payment-terms" minHeight="100px" /></div>
        <div><label className="text-sm font-medium text-slate-600 mb-1 block">Notas por defecto</label>
          <RichTextEditor value={config.default_notes || ""} onChange={v => update("default_notes", v)} disabled={!isAdmin} placeholder="Condiciones especiales, garantias..." testId="config-notes" minHeight="100px" /></div>
      </div>

      {isAdmin && (
        <div className="flex justify-end">
          <Button onClick={() => handleSave()} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-config">
            <FloppyDisk size={16} className="mr-2" /> {saving ? "Guardando..." : "Guardar Configuracion"}
          </Button>
        </div>
      )}
    </div>
  );
}
